import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React from 'react';
import FigureService from '../services/figureService';
import FolderIcon from '@mui/icons-material/Folder';
import { IFigureCollection } from '../models/IFigureCollection';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from '../config/env';
import { IFigureDto } from '../models/IFigureDto';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { getCollectionColors } from '../utils/collectionColors';
import { emitAppEvent } from '../events/appEvents';

interface MoreVertMenuProps {
  onEdit?: () => void;
  onHelp?: () => void;
  figureId?: number;
  collectionId?: number;
  customerId: number;
  className?: string;
  onAddToCollectionHandlerChange?: (handler: (() => void) | undefined) => void;
}

export const MoreVertMenu: React.FC<MoreVertMenuProps> = ({
  onEdit,
  onHelp,
  figureId,
  collectionId,
  customerId,
  className,
  onAddToCollectionHandlerChange,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [collections, setCollections] = React.useState<IFigureCollection[]>([]);
  const [popoverAnchor, setPopoverAnchor] = React.useState<null | HTMLElement>(null);
  const [figureMemberships, setFigureMemberships] = React.useState<Record<string, string>>({});
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();
  const cfg = useConfig();

  const handleAction = React.useCallback(
    async (action: string, event?: React.MouseEvent) => {
      // Store anchor before closing menu for "Legg til i samling"
      const menuItemElement = action === 'Legg til i samling' && event?.currentTarget ? (event.currentTarget as HTMLElement) : null;

      setAnchorEl(null);

      switch (action) {
        case 'Rediger':
          onEdit?.();
          break;
        case 'Legg til i samling':
          try {
            const allCollections = await FigureService.getCollectionsByCustomer(customerId);
            const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
            const defaultId = Number(cfg?.defaultCollectionId ?? 0);

            // Order collections: default first, then others (excluding favorite)
            let defaultCollection = allCollections.find((c) =>
              defaultId > 0 ? c.id === defaultId : (c as IFigureCollection & { isDefault?: boolean }).isDefault === true,
            );
            if (defaultCollection && defaultCollection.id === favoriteId) {
              defaultCollection = undefined;
            }

            const otherCollections = allCollections.filter((c) => c.id !== favoriteId && (!defaultCollection || c.id !== defaultCollection.id));
            const orderedCollections = defaultCollection ? [defaultCollection, ...otherCollections] : otherCollections;

            setCollections(orderedCollections);

            // Fetch figure to get collection memberships for filtering
            if (figureId && collectionId !== undefined) {
              try {
                const figure = await FigureService.getFigureByIdAndCollection(figureId, collectionId);
                setFigureMemberships(figure.collectionMemberships || {});
              } catch (err) {
                console.error('Error fetching figure for memberships:', err);
                setFigureMemberships({});
              }
            } else {
              setFigureMemberships({});
            }

            // Set popover anchor to the menu item that was clicked
            if (menuItemElement) {
              setPopoverAnchor(menuItemElement);
            }
          } catch (error) {
            console.error('Error fetching collections:', error);
          }
          break;
        case 'Fjern fra samling':
          if (figureId && collectionId) {
            try {
              await FigureService.removeFigureFromCollection(collectionId, figureId);
              emitAppEvent('figureRemoved', { removedFigureId: figureId, collectionId });

              // Optimistically update all list/grid caches to prevent flicker
              // Update grid caches (figures-grid)
              const gridQueries = queryClient.getQueriesData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>({
                queryKey: ['figures-grid'],
              });
              gridQueries.forEach(([key, oldData]) => {
                if (!oldData || !oldData.pages) return;
                const keyArr = key as unknown as (string | number | null | undefined)[];
                const keyCollectionId = typeof keyArr[1] === 'number' ? (keyArr[1] as number) : undefined;
                queryClient.setQueryData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>(key as unknown as readonly unknown[], {
                  ...oldData,
                  pages: oldData.pages.map((page) => ({
                    ...page,
                    data: page.data
                      .map((f: IFigureDto) =>
                        f.imageId === figureId
                          ? {
                              ...f,
                              collectionMemberships: Object.fromEntries(
                                Object.entries(f.collectionMemberships).filter(([k]) => Number(k) !== collectionId),
                              ),
                            }
                          : f,
                      )
                      .filter((f: IFigureDto) => (keyCollectionId === collectionId ? f.imageId !== figureId : true)),
                    totalCount:
                      keyCollectionId === collectionId && page.data.some((f: IFigureDto) => f.imageId === figureId)
                        ? Math.max(0, (page.totalCount || 0) - 1)
                        : page.totalCount,
                  })),
                });
              });

              // Update list caches (figures)
              const listQueries = queryClient.getQueriesData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>({
                queryKey: ['figures'],
              });
              listQueries.forEach(([key, oldData]) => {
                if (!oldData || !oldData.pages) return;
                const keyArr = key as unknown as (string | number | null | undefined)[];
                const keyCollectionId = typeof keyArr[1] === 'number' ? (keyArr[1] as number) : undefined;
                queryClient.setQueryData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>(key as unknown as readonly unknown[], {
                  ...oldData,
                  pages: oldData.pages.map((page) => ({
                    ...page,
                    data: page.data
                      .map((f: IFigureDto) =>
                        f.imageId === figureId
                          ? {
                              ...f,
                              collectionMemberships: Object.fromEntries(
                                Object.entries(f.collectionMemberships).filter(([k]) => Number(k) !== collectionId),
                              ),
                            }
                          : f,
                      )
                      .filter((f: IFigureDto) => (keyCollectionId === collectionId ? f.imageId !== figureId : true)),
                    totalCount:
                      keyCollectionId === collectionId && page.data.some((f: IFigureDto) => f.imageId === figureId)
                        ? Math.max(0, (page.totalCount || 0) - 1)
                        : page.totalCount,
                  })),
                });
              });
            } catch (error) {
              console.error('Error removing figure from collection:', error);
            }
          }
          break;
        case 'Hjelp':
          onHelp?.();
          break;
      }
    },
    [onEdit, customerId, queryClient, figureId, collectionId, onHelp, cfg?.defaultCollectionId, cfg?.favoritterCollectionId],
  );

  const handleActionRef = React.useRef(handleAction);
  React.useEffect(() => {
    handleActionRef.current = handleAction;
  }, [handleAction]);

  React.useEffect(() => {
    if (!onAddToCollectionHandlerChange) return;
    const handler = () => {
      void handleActionRef.current('Legg til i samling');
    };
    onAddToCollectionHandlerChange(handler);
    return () => {
      onAddToCollectionHandlerChange(undefined);
    };
  }, [onAddToCollectionHandlerChange]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleCollectionSelect = async (selectedCollectionId: number) => {
    setPopoverAnchor(null);
    if (figureId) {
      try {
        await FigureService.addFigureToCollection(selectedCollectionId, figureId);
        // Mark the target collection query as stale and refetch on view
        queryClient.invalidateQueries({ queryKey: ['figures-grid', selectedCollectionId] });
        // Also refresh the source collection so avatars reflect new membership
        if (collectionId !== undefined) {
          queryClient.invalidateQueries({ queryKey: ['figures-grid', collectionId] });
          queryClient.invalidateQueries({ queryKey: ['figures', collectionId] });
        }
        // Fetch the fresh figure and notify listeners so FigureDetail updates avatars immediately
        if (collectionId !== undefined) {
          try {
            const fresh = await FigureService.getFigureByIdAndCollection(figureId, collectionId);
            emitAppEvent('figureUpdated', fresh);
          } catch (err) {
            console.error('Failed to refresh figure after adding to collection:', err);
          }
        }
      } catch (error) {
        console.error('Error adding figure to collection:', error);
      }
    }
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        className={className}
        sx={{
          padding: '4px',
        }}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            borderRadius: '8px',
            '& .MuiMenuItem-root': {
              fontSize: '0.875rem',
              padding: '8px 16px',
              minHeight: '40px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <MenuItem onClick={(e) => handleAction('Rediger', e)} disabled={!onEdit}>
          Rediger
        </MenuItem>
        <MenuItem onClick={(e) => handleAction('Legg til i samling', e)} disabled={!figureId}>
          Legg til i samling
        </MenuItem>
        <MenuItem onClick={(e) => handleAction('Fjern fra samling', e)} disabled={!figureId || !collectionId}>
          Fjern fra samling
        </MenuItem>
        <MenuItem onClick={(e) => handleAction('Hjelp', e)} disabled={!onHelp}>
          Hjelp
        </MenuItem>
      </Menu>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxHeight: 300,
            width: '20ch',
            mt: 0.5,
            overflow: 'hidden',
          },
        }}>
        <List
          sx={{
            py: 0,
            maxHeight: 300,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          {collections
            .filter((collection) => {
              const defaultId = Number(cfg?.defaultCollectionId ?? 0);
              const isDefaultById = defaultId > 0 && collection.id === defaultId;
              const isDefaultFlag = (collection as IFigureCollection & { isDefault?: boolean }).isDefault === true;
              return !(isDefaultById || isDefaultFlag);
            })
            .filter((collection) => {
              // Filter out collections the figure is already in
              // figureMemberships uses numeric keys (from collectionMemberships)
              const isAlreadyMember = figureMemberships[collection.id] !== undefined || figureMemberships[String(collection.id)] !== undefined;
              return !isAlreadyMember;
            })
            .map((collection) => {
              const collectionColor = getCollectionColors({
                id: String(collection.id),
                name: collection.name,
                color: collection.color,
              });
              return (
                <ListItem key={collection.id} disablePadding>
                  <ListItemButton
                    onClick={() => {
                      void handleCollectionSelect(collection.id);
                    }}
                    sx={{
                      py: 1,
                      px: 1.5,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FolderIcon
                        sx={{
                          color: collectionColor.bg,
                          fontSize: '1.2rem',
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={collection.name}
                      secondary={collection.comment}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.75rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          {collections.filter((collection) => {
            const defaultId = Number(cfg?.defaultCollectionId ?? 0);
            const isDefaultById = defaultId > 0 && collection.id === defaultId;
            const isDefaultFlag = (collection as IFigureCollection & { isDefault?: boolean }).isDefault === true;
            const isAlreadyMember = figureMemberships[collection.id] || figureMemberships[String(collection.id)];
            return !(isDefaultById || isDefaultFlag) && !isAlreadyMember;
          }).length === 0 && (
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemText primary="Ingen samlinger tilgjengelig" primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>
    </>
  );
};
